import { useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, FileUp, X } from "lucide-react";
import { Dialog } from "@/components/common/Dialog";
import { ImportarLoteForm } from "@/components/leads/ImportarLoteForm";
import { useToast } from "@/lib/toast";
import { useLeadDrawer } from "@/lib/leadDrawer";
import { createLead, fetchAppUsers, type NovoLeadIndividual } from "@/lib/leads";
import { LEAD_PRIORIDADES, LEAD_PRIORIDADE_LABELS } from "@/lib/types";

type Aba = "individual" | "lote";

export function NovoLeadModal({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const [aba, setAba] = useState<Aba>("individual");

  return (
    <Dialog titleId={titleId} onClose={onClose} wide>
      <div className="modal__header">
        <h2 id={titleId}>Novo lead</h2>
        <button type="button" className="btn btn--ghost btn--icon" onClick={onClose} aria-label="Fechar">
          <X className="icon" size={20} aria-hidden="true" />
        </button>
      </div>
      <div className="modal__body">
        <div className="tabs" role="tablist" aria-label="Forma de cadastro">
          <button
            type="button"
            role="tab"
            id="tab-individual"
            aria-selected={aba === "individual"}
            aria-controls="painel-individual"
            className="tab"
            onClick={() => setAba("individual")}
          >
            <Building2 className="icon" size={16} aria-hidden="true" />
            Individual
          </button>
          <button
            type="button"
            role="tab"
            id="tab-lote"
            aria-selected={aba === "lote"}
            aria-controls="painel-lote"
            className="tab"
            onClick={() => setAba("lote")}
          >
            <FileUp className="icon" size={16} aria-hidden="true" />
            Importar em lote
          </button>
        </div>

        <div role="tabpanel" id="painel-individual" aria-labelledby="tab-individual" hidden={aba !== "individual"}>
          <NovoLeadIndividualForm onCriado={onClose} />
        </div>
        <div role="tabpanel" id="painel-lote" aria-labelledby="tab-lote" hidden={aba !== "lote"}>
          {aba === "lote" && <ImportarLoteForm onConcluido={onClose} />}
        </div>
      </div>
    </Dialog>
  );
}

function NovoLeadIndividualForm({ onCriado }: { onCriado: () => void }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { openLead } = useLeadDrawer();
  const usersQuery = useQuery({ queryKey: ["appUsers"], queryFn: fetchAppUsers });

  const [form, setForm] = useState({
    empresa: "",
    ramo: "",
    cidade: "",
    uf: "",
    telefone: "",
    pessoa_contato: "",
    email: "",
    site: "",
    prioridade: "media" as NovoLeadIndividual["prioridade"],
    responsavel_id: "",
    tags: "",
    origem_fonte: "",
    origem_detalhe: "",
    origem_coletada_em: new Date().toISOString().slice(0, 10),
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const obrigatoriosPreenchidos =
    form.empresa.trim() !== "" &&
    form.origem_fonte.trim() !== "" &&
    form.origem_detalhe.trim() !== "" &&
    form.origem_coletada_em !== "";

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const lead = await createLead({
        empresa: form.empresa.trim(),
        ramo: form.ramo.trim() || null,
        cidade: form.cidade.trim() || null,
        uf: form.uf.trim() || null,
        telefone: form.telefone.trim() || null,
        pessoa_contato: form.pessoa_contato.trim() || null,
        email: form.email.trim() || null,
        site: form.site.trim() || null,
        prioridade: form.prioridade,
        responsavel_id: form.responsavel_id || null,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        origem_fonte: form.origem_fonte.trim(),
        origem_detalhe: form.origem_detalhe.trim(),
        origem_coletada_em: form.origem_coletada_em,
      });

      queryClient.invalidateQueries({ queryKey: ["kanban"] });
      queryClient.invalidateQueries({ queryKey: ["filaHoje"] });
      queryClient.invalidateQueries({ queryKey: ["leadsSearch"] });

      showToast({
        message: `Lead "${lead.empresa}" criado.`,
        tone: "success",
        actionLabel: "Ver ficha",
        onAction: () => openLead(lead.id),
      });
      onCriado();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="novo-empresa">Empresa *</label>
          <input
            id="novo-empresa"
            type="text"
            value={form.empresa}
            onChange={(e) => setForm((f) => ({ ...f, empresa: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="novo-ramo">Ramo</label>
          <input
            id="novo-ramo"
            type="text"
            value={form.ramo}
            onChange={(e) => setForm((f) => ({ ...f, ramo: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="novo-cidade">Cidade</label>
          <input
            id="novo-cidade"
            type="text"
            value={form.cidade}
            onChange={(e) => setForm((f) => ({ ...f, cidade: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="novo-uf">UF</label>
          <input
            id="novo-uf"
            type="text"
            maxLength={2}
            value={form.uf}
            onChange={(e) => setForm((f) => ({ ...f, uf: e.target.value.toUpperCase() }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="novo-telefone">Telefone</label>
          <input
            id="novo-telefone"
            type="tel"
            value={form.telefone}
            onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="novo-pessoa">Pessoa de contato</label>
          <input
            id="novo-pessoa"
            type="text"
            value={form.pessoa_contato}
            onChange={(e) => setForm((f) => ({ ...f, pessoa_contato: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="novo-email">E-mail</label>
          <input
            id="novo-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="novo-site">Site</label>
          <input
            id="novo-site"
            type="url"
            value={form.site}
            onChange={(e) => setForm((f) => ({ ...f, site: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="novo-prioridade">Prioridade</label>
          <select
            id="novo-prioridade"
            value={form.prioridade}
            onChange={(e) =>
              setForm((f) => ({ ...f, prioridade: e.target.value as NovoLeadIndividual["prioridade"] }))
            }
          >
            {LEAD_PRIORIDADES.map((p) => (
              <option key={p} value={p}>
                {LEAD_PRIORIDADE_LABELS[p]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="novo-responsavel">Responsável</label>
          <select
            id="novo-responsavel"
            value={form.responsavel_id}
            onChange={(e) => setForm((f) => ({ ...f, responsavel_id: e.target.value }))}
          >
            <option value="">Sem responsável</option>
            {usersQuery.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="novo-tags">Tags (separadas por vírgula)</label>
        <input
          id="novo-tags"
          type="text"
          value={form.tags}
          onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
        />
      </div>

      <fieldset>
        <legend>Origem do dado (obrigatório — LGPD art. 7º, IX)</legend>
        <div className="form-row">
          <div className="field">
            <label htmlFor="novo-origem-fonte">Fonte *</label>
            <input
              id="novo-origem-fonte"
              type="text"
              placeholder="Ex.: Google Maps, indicação"
              value={form.origem_fonte}
              onChange={(e) => setForm((f) => ({ ...f, origem_fonte: e.target.value }))}
            />
          </div>
          <div className="field">
            <label htmlFor="novo-origem-data">Coletado em *</label>
            <input
              id="novo-origem-data"
              type="date"
              max={new Date().toISOString().slice(0, 10)}
              value={form.origem_coletada_em}
              onChange={(e) => setForm((f) => ({ ...f, origem_coletada_em: e.target.value }))}
            />
          </div>
        </div>
        <div className="field">
          <label htmlFor="novo-origem-detalhe">Detalhe (URL ou descrição) *</label>
          <input
            id="novo-origem-detalhe"
            type="text"
            value={form.origem_detalhe}
            onChange={(e) => setForm((f) => ({ ...f, origem_detalhe: e.target.value }))}
          />
        </div>
      </fieldset>

      {erro && (
        <p className="field-error" role="alert">
          {erro}
        </p>
      )}

      <div className="modal__footer" style={{ padding: "16px 0 0" }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!obrigatoriosPreenchidos || salvando}
          onClick={salvar}
        >
          {salvando ? "Salvando…" : "Criar lead"}
        </button>
      </div>
    </div>
  );
}
